import { WhichWeightAPI } from 'WhichWeightAPI'
import { useState, useEffect, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useGetCoaches } from 'redux/customHooks/useGetCoaches';
import { useGetAthletes } from 'redux/customHooks/useGetAthletes';
import { initializeGroupProfileData, initializeAthleteProfileData, resetAthleteProfileState } from 'redux/slices/athleteProfile'
import { setOrganizationAthletes, resetOrganizationGroups } from 'redux/slices/organization'
import { ProfileContext } from 'components/Profile/ProfileContext';
import { ff, flags } from 'functions/roleBasedAccessUtils'

import ScreenFrame from 'components/Frame/ScreenFrame';
import ContentFrame from 'components/Frame/ContentFrame';
import KinesisProfileHeader from 'components/Profile/KinesisProfileHeader';
import OverviewTab from 'components/Profile/OverviewTab';
import GroupAthletesTab from 'components/Profile/GroupAthletesTab';
import DefaultWeeklyScheduleTab from 'components/Profile/DefaultWeeklyScheduleTab'
import FitnessProfileTab from 'components/Profile/FitnessProfileTab'
import EquipmentProfileTab from 'components/Profile/EquipmemtProfileTab';
import InjuriesTab from 'components/Athlete/InjuriesTab'
import AssessmentsTab from 'components/Athlete/AssessmentsTab';
import WorkoutsTab from 'components/Athlete/WorkoutsTab';
import WearablesTab from 'components/Athlete/WearablesTab'

export default function KinesisProfile({ 
    propProfileType = 'individual' 
}) {

    const dispatch = useDispatch()

    const coaches = useGetCoaches()
    const athletes = useGetAthletes()

    const { urlGroupId, urlAthleteId } = useParams()

    const [searchParams, setSearchParams] = useSearchParams()

    const hasKinesisProfileDataLoaded = useRef(false)

    const userType = useSelector(state => state.user.type)
    const organizationId = useSelector(state => state.user.organizationId)
    const profileType = useSelector(state => state.athleteProfile.profileType)
    const groupId = useSelector(state => state.athleteProfile.groupId)
    const athleteId = useSelector(state => state.athleteProfile.userId)
    const athleteUserType = useSelector(state => state.athleteProfile.type)
    const trainingChartPresentDate = useSelector(state => state.athleteProfile.trainingChartPresentDate)
    const trainingChartPresentWeekOfTheYearKey = useSelector(state => state.athleteProfile.trainingChartPresentWeekOfTheYearKey)
    const groupAthletes = useSelector(state => state.athleteProfile.groupAthletes)

    const [kinesisProfileLoading, setKinesisProfileLoading] = useState(true)

    const canEditKinesisProfileEquipmentProfile = ff(flags.canEditKinesisProfileEquipmentProfile, { profileType: profileType })
    const canEditKinesisProfileFitnessProfile = ff(flags.canEditKinesisProfileFitnessProfile, { profileType: profileType })
    const canEditKinesisProfileInjuries = ff(flags.canEditKinesisProfileInjuries, { profileType: profileType })
    const canEditKinesisProfileWeekDaySchedules = ff(flags.canEditKinesisProfileWeekDaySchedules, { profileType: profileType })

    const groupTabs = [
        { key: 'overview', text: 'Overview' },
        { key: 'calendar', text: 'Calendar' },
        { key: 'athletes', text: 'Athletes' },
        { key: 'default-schedule', text: 'Schedule' },
        { key: 'fitness-profile', text: 'Fitness Profile' },
        { key: 'equipment', text: 'Equipment' },
        { key: 'workouts', text: 'Workouts' }
    ]

    const individualTabs = [
        { 
            key: 'overview', 
            text: 'Overview',
            visible: true
        },
        { 
            key: 'calendar', 
            text: 'Calendar',
            visible: true
        },
        { 
            key: 'default-schedule', 
            text: 'Schedule',
            visible: canEditKinesisProfileWeekDaySchedules
        },
        { 
            key: 'fitness-profile', 
            text: 'Fitness Profile',
            visible: canEditKinesisProfileFitnessProfile
        },
        { 
            key: 'equipment', 
            text: 'Equipment',
            visible: canEditKinesisProfileEquipmentProfile
        },
        { 
            key: 'injuries', 
            text: 'Injuries',
            visible: canEditKinesisProfileInjuries
        },
        { 
            key: 'assessments', 
            text: 'Assessments',
            visible: true
        },
        { 
            key: 'workouts', 
            text: 'Workouts',
            visible: true
        },
        { 
            key: 'wearables', 
            text: 'Wearables',
            visible: userType === 'super-admin'
        }
    ]

    var allowedTabKeys = []
    var defaultTabKey = null

    if (propProfileType === 'group') {
        allowedTabKeys = groupTabs.map(tab => tab.key)
        defaultTabKey = groupTabs[0].key
    }

    if (propProfileType === 'individual') {
        allowedTabKeys = individualTabs.filter(tab => tab.visible).map(tab => tab.key)
        defaultTabKey = individualTabs[0].key
    }

    const GetTabFromUrl = () => {
        const tabFromUrl = searchParams.get('tab')
        return tabFromUrl && allowedTabKeys.includes(tabFromUrl) ? tabFromUrl : defaultTabKey
    }

    const HandleTabChange = (newTabKey) => {
        const validTabKey = allowedTabKeys.includes(newTabKey) ? newTabKey : defaultTabKey
        setSelectedTabKey(validTabKey)
        setSearchParams({ tab: validTabKey })
    }
    
    const [selectedTabKey, setSelectedTabKey] = useState(() => GetTabFromUrl())

    const GetKinesisProfileData = async (overrideLocalDate = null) => {
        if (propProfileType === 'group') {
            GetGroupTrainingChartDataFromBackend(overrideLocalDate)
        }
        
        if (propProfileType === 'individual') {
            GetIndividualKinesisProfileData(overrideLocalDate)
        }
    }

    const GetGroupTrainingChartDataFromBackend = async (overrideLocalDate = null) => {

        setKinesisProfileLoading(true)

        dispatch(resetAthleteProfileState())

        const jsonBody = {
            groupId: parseInt(urlGroupId),
            localDate: overrideLocalDate || trainingChartPresentDate
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('PROGRAM', 'GET', '/group_training_chart_data', jsonBody)

        // console.log(result.data.data)

        // Log the size of the training chart data
        // const sizeInBytes = JSON.stringify(result.data.data).length;
        // const sizeInKB = (sizeInBytes / 1024).toFixed(2);
        // console.log(`Training chart data size: ${sizeInKB} KB`);

        if (result.data.status === 'failure') {
            setKinesisProfileLoading(false)
            console.error(result)
            return
        }

        dispatch(initializeGroupProfileData(result.data.data))

        setKinesisProfileLoading(false)
    }

    const GetIndividualKinesisProfileData = async (overrideLocalDate = null) => {

        setKinesisProfileLoading(true)

        dispatch(resetAthleteProfileState())

        const jsonBody = {
            userId: parseInt(urlAthleteId),
            localDate: overrideLocalDate || trainingChartPresentDate,
            includeWorkoutTemplateData: 0
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('PROGRAM', 'GET', '/training_chart_data', jsonBody)

        // console.log(result.data.data)

        // Log the size of the training chart data
        // const sizeInBytes = JSON.stringify(result.data.data).length;
        // const sizeInKB = (sizeInBytes / 1024).toFixed(2);
        // console.log(`Training chart data size: ${sizeInKB} KB`);

        if (result.data.status === 'failure') {
            setKinesisProfileLoading(false)
            console.error(result)
            return
        }

        dispatch(initializeAthleteProfileData(result.data.data))

        setKinesisProfileLoading(false)
    }

    const HandleGroupAthletesSaveSuccess = () => {
        dispatch(setOrganizationAthletes(null))
        dispatch(resetOrganizationGroups())
        GetKinesisProfileData()
    }

    useEffect(() => {
        // Sync selectedTabKey with URL param on mount and when URL changes
        const tabFromUrl = GetTabFromUrl()
        setSelectedTabKey(tabFromUrl)
    }, [searchParams])

    useEffect(() => {

        // Prevents multiple calls to GetIndividualKinesisProfileData() in React Strict Mode

        if (hasKinesisProfileDataLoaded.current) return; 

        // console.log(athleteId, athleteId)

        // If the slice already contains data for this athlete, don't fetch it again

        if (
            athleteId !== null &&
            athleteId !== undefined &&
            urlAthleteId !== null &&
            urlAthleteId !== undefined &&
            athleteId.toString() === urlAthleteId.toString()
        ){ 
            // console.log('Slice already contains data for this athlete, not fetching it again')
            setKinesisProfileLoading(false)
            hasKinesisProfileDataLoaded.current = true;
            return; 
        }

        // console.log('Fetching training chart data from backend for new athlete')

        GetKinesisProfileData()

        hasKinesisProfileDataLoaded.current = true;

    }, [])

    const athleteProfileContextData = {
        ReloadTrainingChartData: GetKinesisProfileData
    }

    if (coaches === null || profileType === null || kinesisProfileLoading) {
        return (
            <ScreenFrame limitWidth={false} contentClassName="h-full flex flex-col">
                <div className='h-28 w-full bg-gray-200 rounded-lg animate-pulse' />

                <div className='flex-1 min-h-0 flex flex-col gap-4'>
                    <div className='flex-1 min-h-0 grid grid-cols-4 gap-4'>
                        <div className="flex flex-col bg-gray-200 rounded-lg animate-pulse" />

                        <div className='col-span-3 flex flex-col bg-gray-200 rounded-lg animate-pulse' />
                    </div>

                    <div className='flex-shrink-0 h-[270px] bg-gray-200 rounded-lg animate-pulse' />
                </div>
            </ScreenFrame>
        )
    }

    if (profileType === 'group' && groupId === null) {
        return null
    }

    if (profileType === 'individual' && athleteId === null) {
        return null
    }

    if (profileType === 'individual' && athleteUserType !== 'trainee') {
        return (
            <ScreenFrame limitWidth={false}>
                <ContentFrame>
                    <div className="p-4 text-center" >
                        <p>Cannot view coach profiles on this page</p>
                    </div>
                </ContentFrame>
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame limitWidth={false}>
            <ProfileContext.Provider value={athleteProfileContextData}>
                <KinesisProfileHeader 
                    groupTabs={groupTabs}
                    individualTabs={individualTabs}
                    selectedTabKey={selectedTabKey} 
                    HandleTabChange={HandleTabChange} 
                />
            
                {selectedTabKey === 'overview' && <OverviewTab selectedTabKey={selectedTabKey} />}
                
                {selectedTabKey === 'calendar' && <OverviewTab selectedTabKey={selectedTabKey} />}

                {profileType === 'group' && selectedTabKey === 'athletes' && (
                    <GroupAthletesTab
                        weekOfTheYearKey={trainingChartPresentWeekOfTheYearKey}
                        organizationId={organizationId}
                        coaches={coaches}
                        athletes={athletes}
                        groupId={groupId}
                        groupAthletes={groupAthletes}
                        OnSaveSuccess={HandleGroupAthletesSaveSuccess}
                    />
                )}
                
                {selectedTabKey === 'default-schedule' && <DefaultWeeklyScheduleTab />}

                {selectedTabKey === 'fitness-profile' && <FitnessProfileTab />}

                {selectedTabKey === 'equipment' && <EquipmentProfileTab />}
                
                {selectedTabKey === 'injuries' && <InjuriesTab />}

                {(profileType === 'group-individual' || profileType === 'individual') && selectedTabKey === 'assessments' && <AssessmentsTab />}

                {selectedTabKey === 'workouts' && <WorkoutsTab />}

                {userType === 'super-admin' && selectedTabKey === 'wearables' && <WearablesTab />} 
            </ProfileContext.Provider>
        </ScreenFrame>
    )
}
